'use client';

import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, X } from 'lucide-react';
import styles from './CurrencySelect.module.scss';
const MOBILE_SHEET_QUERY = '(max-width: 768px)';
export interface LanguageOption {
  label: string;
  value: string;
}
export interface LanguageSelectProps {
  label: string;
  value: string;
  options: LanguageOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  hideLabel?: boolean;
  inset?: boolean;
  triggerVariant?: 'default' | 'locale';
}
const LanguageSelect: FC<LanguageSelectProps> = ({
  label,
  value,
  options,
  onChange,
  disabled = false,
  hideLabel = false,
  inset = false,
  triggerVariant = 'default'
}) => {
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [isMobileSheet, setIsMobileSheet] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    setMounted(true);
  }, []);
  useEffect(() => {
    const media = window.matchMedia(MOBILE_SHEET_QUERY);
    const sync = () => setIsMobileSheet(media.matches);
    sync();
    media.addEventListener('change', sync);
    return () => media.removeEventListener('change', sync);
  }, []);
  const close = useCallback(() => {
    setOpen(false);
  }, []);
  useEffect(() => {
    if (!open || !isMobileSheet) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open, isMobileSheet]);
  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (rootRef.current?.contains(target) || panelRef.current?.contains(target)) {
        return;
      }
      close();
    };
    document.addEventListener('keydown', handleKey);
    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [open, close]);
  const selected = useMemo(() => options.find(o => o.value === value), [options, value]);
  const handleSelect = (next: string) => {
    onChange(next);
    close();
  };
  const panelContent = <>
            {isMobileSheet && <div className={styles.header}>
                    <h3 className={styles.title}>{label}</h3>
                    <button type="button" className={styles.closeButton} onClick={close} aria-label="Close">
                        <X size={18} strokeWidth={2} />
                    </button>
                </div>}

            <ul className={styles.list} role="listbox">
                {options.map(option => {
        const isActive = option.value === value;
        return <li key={option.value} role="option" aria-selected={isActive}>
                            <button type="button" className={`${styles.item} ${isActive ? styles.itemActive : ''}`} onClick={() => handleSelect(option.value)}>
                                <span className={styles.itemText}>
                                    <span className={styles.code}>{option.label}</span>
                                </span>
                                {isActive && <Check size={18} strokeWidth={2.5} className={styles.check} />}
                            </button>
                        </li>;
      })}
            </ul>
        </>;
  const mobileSheet = open && isMobileSheet ? <div className={styles.overlay} onClick={close} role="presentation">
                <div ref={panelRef} className={`${styles.panel} ${styles.panelSheet}`} role="dialog" aria-modal="true" aria-label={label} onClick={e => e.stopPropagation()}>
                    {panelContent}
                </div>
            </div> : null;
  return <div ref={rootRef} className={`${styles.field} ${inset ? styles.fieldInset : ''}`} data-sentry-component="LanguageSelect" data-sentry-source-file="LanguageSelect.tsx">
            {!hideLabel && <span className={styles.label}>{label}</span>}
            <button type="button" className={`${styles.trigger} ${triggerVariant === 'locale' ? styles.triggerLocale : ''}`} onClick={() => !disabled && setOpen(prev => !prev)} aria-haspopup="listbox" aria-expanded={open} disabled={disabled}>
                <span className={styles.triggerValue}>
                    {selected?.label || '—'}
                </span>
                <ChevronDown size={16} className={`${styles.chevron} ${open ? styles.chevronOpen : ''}`} data-sentry-element="ChevronDown" data-sentry-source-file="LanguageSelect.tsx" />
            </button>

            {open && !isMobileSheet && <div ref={panelRef} className={`${styles.panel} ${styles.panelDropdown}`} role="dialog" aria-label={label}>
                    {panelContent}
                </div>}

            {mounted && mobileSheet ? createPortal(mobileSheet, document.body) : null}
        </div>;
};
export default LanguageSelect;